// Import Libraries
import React, { useEffect, useState, useContext, memo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

// Pages
import ShotList from './mainScriptPage';
import AccessRequired from '../accessRequired.js';

// Context
import { ScriptProvider } from '../../context/Script';
import { ProjectViewProvider } from '../../context/ProjectView';
import { UserContext } from '../../context/User';

// Services
import versionService from '../../services/versionService.js';

// Construct Component
const ViewScriptDetails = memo(({ scriptId: propScriptId }) => {
  const { scriptId: paramRootScriptId, versionMinor } = useParams();
  const rootScriptId = propScriptId || paramRootScriptId;
  // The major version's URL has no `/v/:versionMinor` segment, so switching
  // to it from a route that's already on the bare URL (e.g. landing here
  // from the Dashboard, which restored a different version) navigates to an
  // identical pathname. useParams() doesn't change in that case, so without
  // this, the resolution effect below never re-fires and the just-activated
  // version is never consumed. location.key is fresh on every navigate()
  // call regardless of whether the pathname actually changed.
  const location = useLocation();
  const { user } = useContext(UserContext);
  const { isLoading } = useAuth0();
  // HeaderBar separately navigates a signed-out visitor away from /app/ URLs,
  // but that's a side effect in a sibling component racing this one's own
  // data fetch -- relying on it left signed-out visitors stuck on the
  // "Bear with us..." spinner forever when this route won the race. Render
  // the access prompt directly instead of ever starting the fetch.
  const isLoggedOut = !isLoading && (!user || !user.id);

  // The URL/route only ever carries the project's root id (+ an optional
  // version number) -- never a version's own internal script id. This
  // resolves that pair to the real script id every existing data-fetching
  // call already expects, so nothing downstream of ShotList needs to know
  // versioning exists.
  const [activeScriptId, setActiveScriptId] = useState(null);
  // Set when the backend confirms this account isn't a collaborator on the
  // project (403 from versionService.activate), as opposed to any other
  // resolution failure, which still falls back to the root script id below.
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setActiveScriptId(null);
    setAccessDenied(false);

    if (isLoggedOut) return;

    if (rootScriptId) {
      // If a version was just explicitly activated (e.g. the version chip)
      // and this effect is only firing because of the navigation that
      // followed, reuse that result instead of re-resolving. Re-resolving
      // here isn't just redundant -- the major version's URL has no
      // `/v/:versionMinor` segment, so re-resolving with no minor is
      // indistinguishable from "no version requested, restore whatever was
      // last persisted" and can land on the wrong version.
      const justActivated = versionService.consumeJustActivated(rootScriptId);
      if (justActivated) {
        setActiveScriptId(justActivated.id);
        return () => {
          cancelled = true;
        };
      }

      versionService
        .activate(rootScriptId, versionMinor !== undefined ? Number(versionMinor) : undefined)
        .then((version) => {
          if (!cancelled) setActiveScriptId(version.id);
        })
        .catch((err) => {
          if (err.status === 403) {
            console.error('User does not have access to this project', err);
            if (!cancelled) setAccessDenied(true);
            return;
          }
          console.error('Error resolving active version, falling back to root script id', err);
          if (!cancelled) setActiveScriptId(Number(rootScriptId));
        });
    }

    return () => {
      cancelled = true;
    };
  }, [rootScriptId, versionMinor, isLoggedOut, location.key]);

  if (isLoggedOut) return <AccessRequired />;
  if (accessDenied) return <AccessRequired variant="noAccess" />;
  if (!activeScriptId) return null;

  return (
    <ScriptProvider>
      <ProjectViewProvider>
        <ShotList scriptId={activeScriptId} />
      </ProjectViewProvider>
    </ScriptProvider>
  );
});

export default ViewScriptDetails;
